import type { OakStreamInternalOptions } from "./types";

import { createStreamHash } from "./hash";
import { Stream } from "./stream";

// A "connected" stream that hasn't seen any traffic for this long is treated
// as silently dead — common when the OS hasn't surfaced a TCP RST yet (mobile
// roaming, NAT timeout). The 5s backend heartbeat means a healthy connection
// will always beat this threshold.
const STALE_STREAM_MS = 30_000;

// How often the watchdog scans cached streams. Lower frequency than the
// stale threshold so a slow heartbeat doesn't trigger false positives.
const WATCHDOG_INTERVAL_MS = 10_000;

export class StreamCache {
  private readonly streams = new Map<string, Stream>();
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private visibilityListener: (() => void) | null = null;
  private pageshowListener: (() => void) | null = null;

  build(options: OakStreamInternalOptions) {
    const hash = createStreamHash(options);
    const existingStream = this.streams.get(hash);

    if (existingStream === undefined) {
      const nextStream = new Stream({
        hash,
        url: options.url,
        getIdTokenRefreshIfExpired: options.getIdTokenRefreshIfExpired,
        initialLastEventId: options.initialLastEventId,
        removeFromCache: (streamHash) => {
          this.remove(streamHash);
        },
      });

      this.streams.set(hash, nextStream);
      // Lifecycle listeners are lazy: they only register while at least one
      // stream lives in the cache. This prevents idle caches (e.g. across
      // unrelated tests) from holding listeners or intervals on `document`.
      this.installLifecycleListeners();

      return nextStream;
    }

    return existingStream;
  }

  get(hash: string) {
    return this.streams.get(hash);
  }

  remove(hash: string) {
    this.streams.delete(hash);
    if (this.streams.size === 0) {
      this.removeLifecycleListeners();
    }
  }

  close() {
    const cachedStreams = [...this.streams.values()];

    this.streams.clear();

    for (const stream of cachedStreams) {
      stream.close();
    }

    this.removeLifecycleListeners();
  }

  private resumeAll(): void {
    for (const stream of this.streams.values()) {
      stream.resume();
    }
  }

  private checkStaleAll(): void {
    for (const stream of this.streams.values()) {
      stream.checkStale(STALE_STREAM_MS);
    }
  }

  private installLifecycleListeners(): void {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }
    if (this.watchdog !== null || this.visibilityListener !== null) {
      return;
    }

    this.visibilityListener = () => {
      if (document.visibilityState === "visible") {
        this.resumeAll();
      }
    };
    this.pageshowListener = () => {
      this.resumeAll();
    };

    document.addEventListener("visibilitychange", this.visibilityListener);
    window.addEventListener("pageshow", this.pageshowListener);
    this.watchdog = setInterval(() => {
      this.checkStaleAll();
    }, WATCHDOG_INTERVAL_MS);
  }

  private removeLifecycleListeners(): void {
    if (this.watchdog !== null) {
      clearInterval(this.watchdog);
      this.watchdog = null;
    }
    if (typeof window !== "undefined" && this.pageshowListener) {
      window.removeEventListener("pageshow", this.pageshowListener);
      this.pageshowListener = null;
    }
    if (typeof document !== "undefined" && this.visibilityListener) {
      document.removeEventListener("visibilitychange", this.visibilityListener);
      this.visibilityListener = null;
    }
  }
}

export function createOakStreamClient() {
  return new StreamCache();
}
